/* ==========================================================================
   LINDIE Collection Row — the carousel for a single, merchant-picked collection.

   A deliberate, trimmed copy of the arrow logic in
   section-lindie-fiction-rows.js rather than a shared file. That section is
   not to be touched, and sharing its script would mean two <script> tags for
   the same src on any page carrying both: the browser would execute the IIFE
   twice and double-bind its document-level listeners, which have no re-entry
   guard.

   What was dropped, because a single collection has no use for it: the tab
   machinery, the batched/deferred row loader, and the hash / sessionStorage
   tab handoff. What remains is the scroll maths and the arrow + fade state.

   Namespaced .lcr-* / data-lcr-* throughout, so nothing here can bind to a
   Fiction Rows element or be bound by its script.
   ========================================================================== */
(function () {
  'use strict';

  /* Sub-pixel slack: scrollLeft and scrollWidth rarely land on exact integers,
     so a strict comparison would leave an arrow visible with nothing to reach. */
  var EDGE = 2;

  function trackGap(track) {
    var gap = parseFloat(window.getComputedStyle(track).columnGap);
    if (isNaN(gap)) gap = parseFloat(window.getComputedStyle(track).gap);
    return isNaN(gap) ? 0 : gap;
  }

  function cardStep(track) {
    var item = track.querySelector('.lcr-row__item');
    if (!item) return 0;
    return item.getBoundingClientRect().width + trackGap(track);
  }

  /* One card per click. The track also carries scroll-snap, which settles the
     rest on the nearest card edge. */
  function scrollStep(track) {
    return cardStep(track) || track.clientWidth;
  }

  function maxScroll(track) {
    return Math.max(track.scrollWidth - track.clientWidth, 0);
  }

  function update(row) {
    var track = row.querySelector('[data-lcr-track]');
    var next = row.querySelector('[data-lcr-next]');
    var prev = row.querySelector('[data-lcr-prev]');
    if (!track || !next) return;

    var max = maxScroll(track);
    var scrollable = max > EDGE;
    var atEnd = track.scrollLeft >= max - EDGE;
    var atStart = track.scrollLeft <= EDGE;

    /* Hidden when there is nothing more to scroll — including the case where
       all cards already fit, so no arrow ever appears on a short row. */
    next.hidden = !scrollable || atEnd;

    /* The back arrow appears only once the row has actually been scrolled, so
       a row at rest looks exactly as it did before. */
    if (prev) prev.hidden = !scrollable || atStart;

    /* Each edge fade follows exactly the same state as the arrow on that side. */
    row.classList.toggle('lcr-row--has-more', !next.hidden);
    row.classList.toggle('lcr-row--has-prev', !!prev && !prev.hidden);

    markPeeking(track);
  }

  /* A card clipped by either edge of the track is only partly on screen, and an
     arrow usually sits over it. Hovering a sliver like that pops an overlay and
     two buttons the reader can neither see properly nor aim at, so those cards
     are flagged and the stylesheet suppresses their hover.

     Measured against the TRACK's box, not the viewport: the track is what does
     the clipping. EDGE absorbs sub-pixel rounding, so a fully-visible card
     sitting exactly on a boundary is not flagged by mistake. */
  function markPeeking(track) {
    var box = track.getBoundingClientRect();

    track.querySelectorAll('.lcr-row__item').forEach(function (item) {
      var r = item.getBoundingClientRect();
      var clipped = r.left < box.left - EDGE || r.right > box.right + EDGE;
      item.classList.toggle('lcr-row__item--peek', clipped);
    });
  }

  function bind(row) {
    if (!row || row.dataset.lcrBound === 'true') return;

    var track = row.querySelector('[data-lcr-track]');
    var next = row.querySelector('[data-lcr-next]');
    var prev = row.querySelector('[data-lcr-prev]');
    if (!track || !next) return;

    row.dataset.lcrBound = 'true';

    var refresh = function () { update(row); };

    next.addEventListener('click', function () {
      track.scrollBy({ left: scrollStep(track), behavior: 'smooth' });
    });

    if (prev) {
      prev.addEventListener('click', function () {
        track.scrollBy({ left: -scrollStep(track), behavior: 'smooth' });
      });
    }

    track.addEventListener('scroll', refresh, { passive: true });

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () {
        window.requestAnimationFrame(refresh);
      });
      ro.observe(track);
      track.querySelectorAll('img').forEach(function (img) { ro.observe(img); });
    } else {
      window.addEventListener('resize', refresh, { passive: true });
    }

    /* Cover art changes the track width as it loads. */
    track.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', refresh, { once: true });
    });

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(refresh);
    });

    window.addEventListener('load', refresh, { once: true });
    window.addEventListener('resize', refresh, { passive: true });
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-lcr-row]').forEach(function (row) {
      bind(row);
      update(row);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); }, { once: true });
  } else {
    init(document);
  }

  /* Theme editor re-renders the section; bind() is idempotent per row. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
